/**
 * State machine (pure reducer) for the P2P transfer flow.
 *
 * It is deliberately a flat reducer (not xstate): the actual orchestration
 * (signaling + WebRTC + protocol) lives in the UI layer and only DISPATCHES
 * events; the transition logic here is 100% testable in unit tests.
 * Terminal states (`done`/`error`) are stable: they ignore any event.
 */

export type TransferErrorKind =
  | "invalid-code"
  | "expired"
  | "already-used"
  | "connection-failed"
  | "transfer-failed"
  | "too-large"
  | "network";

export interface TransferProgress {
  transferredBytes: number;
  totalBytes: number;
}

export type SenderState =
  | { phase: "idle" }
  | { phase: "creating" }
  | { phase: "waiting-peer"; code: string; transferCode: string; expiresInSecs: number }
  | { phase: "connecting"; code: string; transferCode: string }
  | { phase: "sending"; progress: TransferProgress }
  | { phase: "done"; inserted: number; skipped: number }
  | { phase: "error"; kind: TransferErrorKind };

export type SenderEvent =
  | { type: "CREATE" }
  | { type: "ROOM_CREATED"; code: string; transferCode: string; expiresInSecs: number }
  | { type: "PEER_JOINED" }
  | { type: "CHANNEL_OPEN" }
  | { type: "PROGRESS"; transferredBytes: number; totalBytes: number }
  | { type: "ACKED"; inserted: number; skipped: number }
  | { type: "FAIL"; kind: TransferErrorKind }
  | { type: "RESET" };

export const SENDER_INITIAL: SenderState = { phase: "idle" };

export function senderReducer(state: SenderState, event: SenderEvent): SenderState {
  if (event.type === "RESET") {
    return SENDER_INITIAL;
  }
  if (event.type === "FAIL") {
    // stable terminal: a later error does not overwrite a `done`
    return state.phase === "done" ? state : { phase: "error", kind: event.kind };
  }
  switch (state.phase) {
    case "idle": {
      return event.type === "CREATE" ? { phase: "creating" } : state;
    }
    case "creating": {
      return event.type === "ROOM_CREATED"
        ? {
            phase: "waiting-peer",
            code: event.code,
            transferCode: event.transferCode,
            expiresInSecs: event.expiresInSecs,
          }
        : state;
    }
    case "waiting-peer": {
      return event.type === "PEER_JOINED"
        ? { phase: "connecting", code: state.code, transferCode: state.transferCode }
        : state;
    }
    case "connecting": {
      return event.type === "CHANNEL_OPEN"
        ? { phase: "sending", progress: { transferredBytes: 0, totalBytes: 0 } }
        : state;
    }
    case "sending": {
      if (event.type === "PROGRESS") {
        return {
          phase: "sending",
          progress: { transferredBytes: event.transferredBytes, totalBytes: event.totalBytes },
        };
      }
      if (event.type === "ACKED") {
        return { phase: "done", inserted: event.inserted, skipped: event.skipped };
      }
      return state;
    }
    case "done":
    case "error": {
      return state;
    }
    default: {
      return state;
    }
  }
}

export type ReceiverState =
  | { phase: "idle" }
  | { phase: "joining" }
  | { phase: "connecting" }
  | { phase: "receiving"; progress: TransferProgress }
  | { phase: "importing" }
  | { phase: "done"; inserted: number; skipped: number }
  | { phase: "error"; kind: TransferErrorKind };

export type ReceiverEvent =
  | { type: "JOIN" }
  | { type: "JOINED" }
  | { type: "CHANNEL_OPEN" }
  | { type: "PROGRESS"; transferredBytes: number; totalBytes: number }
  | { type: "IMPORT_STARTED" }
  | { type: "IMPORTED"; inserted: number; skipped: number }
  | { type: "FAIL"; kind: TransferErrorKind }
  | { type: "RESET" };

export const RECEIVER_INITIAL: ReceiverState = { phase: "idle" };

export function receiverReducer(state: ReceiverState, event: ReceiverEvent): ReceiverState {
  if (event.type === "RESET") {
    return RECEIVER_INITIAL;
  }
  if (event.type === "FAIL") {
    return state.phase === "done" ? state : { phase: "error", kind: event.kind };
  }
  switch (state.phase) {
    case "idle": {
      return event.type === "JOIN" ? { phase: "joining" } : state;
    }
    case "joining": {
      return event.type === "JOINED" ? { phase: "connecting" } : state;
    }
    case "connecting": {
      return event.type === "CHANNEL_OPEN"
        ? { phase: "receiving", progress: { transferredBytes: 0, totalBytes: 0 } }
        : state;
    }
    case "receiving": {
      if (event.type === "PROGRESS") {
        return {
          phase: "receiving",
          progress: { transferredBytes: event.transferredBytes, totalBytes: event.totalBytes },
        };
      }
      if (event.type === "IMPORT_STARTED") {
        return { phase: "importing" };
      }
      if (event.type === "IMPORTED") {
        return { phase: "done", inserted: event.inserted, skipped: event.skipped };
      }
      return state;
    }
    case "importing": {
      return event.type === "IMPORTED"
        ? { phase: "done", inserted: event.inserted, skipped: event.skipped }
        : state;
    }
    case "done":
    case "error": {
      return state;
    }
    default: {
      return state;
    }
  }
}
